import { MapPosition } from '@src/app/models/MapPosition';
import {CesiumAdapterService} from "../cesiumAdapter21/cesium-adapter.service";
import {ChildOverlayCollectionInterface} from "./ChildOverlayCollectionInterface";
import {OverlayCollectionFactoryInterface} from "./OverlayCollectionFactoryInterface";
import {Subject, timer} from "rxjs";
import {debounceTime} from "rxjs/operators";

export class Overlay {

  static PENDING_OVERLAY_ID = -1;
  static STATUS_ACTIVE = 'active';
  static STATUS_PENDING = 'pending';

  public name:string = null; // for <app-overlay> component
  public group:string = null;
  public is_floor_layout:string = null;

  public id:number = null;
  public left_top_lat:number = null;
  public left_top_lon:number = null;
  public right_bottom_lat:number = null;
  public right_bottom_lon:number = null;
  public pendingCadConfig = null;
  public sort = null;
  public rotation:number = null;
  public imageUrl:string = null;
  private image:string = null;
  private parts_config:any = null;
  private min_zoom:number = null;
  private max_zoom:number = null;
  private parent_id:number = null;
  private initial_opacity:number = 1;
  private pendingOverlays = [];
  private currentZoomLevel = null;

  public isVisible: boolean = true;

  private _onOverlayDrawed = new Subject<any>();
  get onOverlayDrawed(){
    return this._onOverlayDrawed.asObservable();
  }

  public status:string = 'active';
  public opacity:number = 0;

  /**
   * Link tile URL to their entity ID in view
   */
  private entityIds:Map<string, any> = new Map();

  public children:ChildOverlayCollectionInterface;
  public isDrawn = false;

  constructor(
    overlayConfig:Object,
    private view:CesiumAdapterService,
    overlayCollectionFactory:OverlayCollectionFactoryInterface
  ) {
    this.children = overlayCollectionFactory.get();
    this.initializeFromObject(overlayConfig);
    this.opacity = (this.initial_opacity === null ? 1 : this.initial_opacity);
    // this.opacity = 0;

    this.onOverlayDrawed.subscribe(({url, id})=>{
      this.pendingOverlays.slice(this.pendingOverlays.indexOf(url), 1);
      this.showOverlayById(id);
    });
  }

  public getId() { return this.id; }
  public getParentId() { return this.parent_id; }
  public getRotation() { return (this.rotation ? this.rotation : 0); }
  public getImage() { return (this.image); }
  public isActive() { return (this.status == 'active'); }
  public isFloorLayout() { return this.is_floor_layout && this.isActive(); }
  public getEntityIds() { return this.entityIds; }

  /**
   * Overlay may be drawn only once
   */
  public draw() {
    if (this.isDrawn) {
      throw new Error("Overlay.draw() may be called only once.");
    }
    if (this.status === Overlay.STATUS_PENDING) {
      return;
    }
    this.isDrawn = true;
    if (this.children.length > 0) {
      this.children.drawAll();
    } else {
      // listen zoom changes
      this.view.zoom.pipe(debounceTime(1000)).subscribe((level) => {
        console.log('zoom callback', level);
        this.currentZoomLevel = level;
        // if (!this.parent_id){
          this.isVisibleInZoom(level) ? this.show() : this.hide();
        // }
      });

      this.view.cameraEventMoveEnd.pipe(debounceTime(1000)).subscribe((level) => {
        this.isVisibleInZoom(this.currentZoomLevel) ? this.show() : this.hide();
      });
    }
  }

  public setOpacity(v) {
    this.opacity = v;
    this.forEachEntity((id) => { this.view.setOverlayOpacity(id, this.opacity); });
    this.children.forEach((overlay:Overlay) => { overlay.setOpacity(v); })
  }

  public hide() {
    let childrenArr = this.children.asArray();
    this.forEachEntity((id) => {
      this.view.hideOverlay(id);
    });
    this.children.forEach((overlay)=>{
      overlay.isVisible = false;
      overlay.hide();
    });
  }

  public show(cb?) {
    console.log(`Showing overlay ${this.getId()} with a rotation ${this.getRotation()}`);
    console.log('cb', cb);
    if (!this.isVisibleInZoom(this.view.currentZoom)){
      console.log('return not in visible zoom');
      return;
    }
    if (this.opacity === 0) {
      console.log('return this.opacity === 0');
      return;
    }
    if (this.children.length == 0) {
      console.log(' if (this.children.length == 0) {');
      if (this.canShowInParts()) {
        console.log(' canShowInParts');
        this.drawParts();
      } else {
        if (this.entityIds.size == 0) { // nothing has been drawn yet. Draw it first
          console.log(' this.entityIds.size == 0');
          this.drawWholeImage(cb);
        } else { // there is hidden overlay. Show it.
          console.log(' this.forEachEntit', this.entityIds);

          this.forEachEntity((id) => { this.showOverlayById(id); if (cb) {cb(id)}});
        }
      }
    }
    else{
      console.log('else children', this.children);
      this.children.forEach((overlay)=>{
        overlay.isVisible = true;
      });
      if (!this.isDrawn) {
        console.log('!this.isDrawn');
        return this.draw();
      }
      console.log('else ', this.children);
      this.children.forEach((overlay)=>{
        overlay.isVisible = true;
        overlay.show();
      });
    }
  }

  public moveCameraIntoView(): void {
    const midPoint = ([x1, y1], [x2, y2]): MapPosition =>
      new MapPosition({
        lat: (x1 + x2) / 2,
        lng: (y1 + y2) / 2,
        height: 600,
      });

    const position: MapPosition = midPoint(
      [this.left_top_lat, this.left_top_lon],
      [this.right_bottom_lat, this.right_bottom_lon]
    );

    this.view.moveCameraTo(position);
  }

  /**
   * Copy properties from standard object to this class
   * @param overlayConfig
   */
  protected initializeFromObject(overlayConfig:Object) {
    if (overlayConfig) {
      for (let i in overlayConfig) {
        if (overlayConfig.hasOwnProperty(i)) {
          if (this.hasOwnProperty(i)) {
            this[i] = overlayConfig[i];
          }
        }
      }
    }
  }

  /**
   * Overlay initially has 1 big image. If image wasn't cut into parts or was rotated
   * we show it.
   */
  protected drawWholeImage(cb?) {
    this.drawFromImageUrl(
      this.imageUrl,
      [this.left_top_lat, this.left_top_lon, this.right_bottom_lat, this.right_bottom_lon],
      cb
    );
  }

  /**
   * Overlay initially has 1 big image. If image has been cut into parts and wasn't rotated
   * we show parts.
   */
  protected drawParts() {
    console.log('drawParts', this.parts_config);
    for (let url in this.parts_config) {
      if (!this.parts_config.hasOwnProperty(url)) { continue; }
      if (this.entityIds.has(url)) { // show already drawn parts
        console.log('this.entityIds.has(url)', this.entityIds.get(url), Overlay.PENDING_OVERLAY_ID);
        if (this.entityIds.get(url) == Overlay.PENDING_OVERLAY_ID){
          return;
        }
        this.showOverlayById(this.entityIds.get(url));
      } else { // draw only visible parts
        console.log('this.parts_config[url]', this.parts_config[url]);
        console.log('drawParts else', this.view.isOverlayInViewbox(this.parts_config[url]));

        if (this.view.isOverlayInViewbox(this.parts_config[url])) {
          console.log('this.drawFromImageUrl', url, this.parts_config[url]);
          this.drawFromImageUrl(url, this.parts_config[url]);
        }
      }
    }
  }

  /**
   * Draw overlay (or its part) using image URL and rectangle coordinates.
   * @param url
   * @param coordinates [lat1, lng1, lat2, lng2]
   */
  public drawFromImageUrl(url, coordinates, cb?) {
    this.entityIds.set(url, Overlay.PENDING_OVERLAY_ID);
    this.pendingOverlays.push(url);
    let overlayHeight = this.isFloorLayout() ? (+window['EVENT_ID'] == 118633 || +window['EVENT_ID'] == 130001) ? 5: .1 : undefined;
    this.view.drawOverlay(
      url,
      Math.min(coordinates[1], coordinates[3]), // west - lng
      Math.min(coordinates[0], coordinates[2]), // south - lat
      Math.max(coordinates[1], coordinates[3]), // east - lng
      Math.max(coordinates[0], coordinates[2]), // north - lat
      this.opacity,
      this.getRotation(),
      overlayHeight,
      this.isFloorLayout(),
      this.sort
    ).then((id) => {
      if (id == -1){
        return;
      }

      this._onOverlayDrawed.next({url, id});
      if (!this.isVisible) {
        this.hide();
      }
      this.view.updateScene();
      this.view.setActiveOverlay(id);
      timer(200).subscribe(()=>{
        if (cb){
          cb(id);
        }
      });
      return this.entityIds.set(url, id)
    });
  }

  protected isVisibleInZoom(zoom) {
    if (!zoom) {
      return false;
    }
    if (!this.isVisible) {
      return false;
    }
    if (this.min_zoom && this.min_zoom > zoom) {
      return false;
    }
    if (this.max_zoom && this.max_zoom < zoom) {
      return false;
    }
    return true;
  }

  public forEachEntity(cb) {
    this.entityIds.forEach((id) => {
      if (id == Overlay.PENDING_OVERLAY_ID) { return; }
      cb(id);
    });
  }

  protected canShowInParts() {
    return (this.parts_config && Object.keys(this.parts_config).length > 0 && !this.getRotation())
  }

  public showOverlayById(id){
    (this.isVisibleInZoom(this.currentZoomLevel)) ? this.view.showOverlay(id) : '';
  }

  public asSimpleObject(){
    return {
      id: this.id,
      opacity: this.opacity,
      rotation: this.rotation,
      left_top_lat: this.left_top_lat,
      left_top_lon: this.left_top_lon,
      right_bottom_lat: this.right_bottom_lat,
      right_bottom_lon: this.right_bottom_lon,
    };
  }
}


